import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Enums, Json, Tables } from './database.types';
import type { GroupInvite, MembershipStatus } from './groups';
import { useSupabase } from './provider';

export type Campaign = Tables<'campaigns'>;
export type CampaignRole = Enums<'campaign_role'>;

export interface CampaignSettings {
  joinViaCode: boolean;
  autoApproveMembers: boolean;
  autoApproveRosters: boolean;
  /** Null means no limit. */
  maxRostersPerMember: number | null;
  allowSoloGames: boolean;
  allowValidatedGames: boolean;
}

/** Mirrors private.default_campaign_settings() in the database. */
export const DEFAULT_CAMPAIGN_SETTINGS: CampaignSettings = {
  joinViaCode: true,
  autoApproveMembers: true,
  autoApproveRosters: false,
  maxRostersPerMember: 1,
  allowSoloGames: true,
  allowValidatedGames: true,
};

const BOOLEAN_KEYS = [
  'joinViaCode',
  'autoApproveMembers',
  'autoApproveRosters',
  'allowSoloGames',
  'allowValidatedGames',
] as const;

/** Settings with every key present: missing or mistyped values fall back to the defaults. */
export function readCampaignSettings(campaign: { settings: Json }): CampaignSettings {
  const raw = campaign.settings;
  const s = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const result: CampaignSettings = { ...DEFAULT_CAMPAIGN_SETTINGS };
  for (const key of BOOLEAN_KEYS) {
    const value = s[key];
    if (typeof value === 'boolean') result[key] = value;
  }
  const max = s.maxRostersPerMember;
  if (max === null || (typeof max === 'number' && Number.isInteger(max))) result.maxRostersPerMember = max;
  return result;
}

export interface CampaignMember {
  userId: string;
  role: CampaignRole;
  status: MembershipStatus;
  joinedOn: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface MyCampaign extends Campaign {
  /** Null when the user has no membership row (an open campaign in one of their groups). */
  myRole: CampaignRole | null;
  myStatus: MembershipStatus | null;
  groupName: string;
}

export const campaignKeys = {
  all: ['campaigns'] as const,
  mine: (userId: string | undefined) => ['campaigns', 'mine', userId] as const,
  group: (groupId: string) => ['campaigns', 'group', groupId] as const,
  one: (id: string) => ['campaigns', id] as const,
  members: (id: string) => ['campaigns', id, 'members'] as const,
  invites: (id: string) => ['campaigns', id, 'invites'] as const,
} as const;

/** Every campaign the user can see (theirs, plus open ones in their groups), with their role and status. */
export function useMyCampaigns(userId: string | undefined) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: campaignKeys.mine(userId),
    enabled: Boolean(userId),
    queryFn: async (): Promise<MyCampaign[]> => {
      if (!userId) return [];
      const [campaigns, mine] = await Promise.all([
        supabase.from('campaigns').select('*, groups(name)').order('name'),
        supabase.from('campaign_members').select('campaign_id, role, status').eq('user_id', userId),
      ]);
      if (campaigns.error) throw campaigns.error;
      if (mine.error) throw mine.error;
      const byId = new Map(mine.data.map((m) => [m.campaign_id, m]));
      return campaigns.data.map(({ groups, ...c }) => ({
        ...c,
        groupName: groups?.name ?? '',
        myRole: byId.get(c.id)?.role ?? null,
        myStatus: byId.get(c.id)?.status ?? null,
      }));
    },
  });
}

export function useGroupCampaigns(groupId: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: campaignKeys.group(groupId),
    enabled: Boolean(groupId),
    queryFn: async (): Promise<Campaign[]> => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('group_id', groupId)
        .order('name');
      if (error) throw error;
      return data;
    },
  });
}

export function useCampaign(id: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: campaignKeys.one(id),
    enabled: Boolean(id),
    queryFn: async (): Promise<Campaign | null> => {
      const { data, error } = await supabase.from('campaigns').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

const ROLE_ORDER: Record<CampaignRole, number> = { owner: 0, admin: 1, moderator: 2, member: 3 };

export function useCampaignMembers(id: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: campaignKeys.members(id),
    enabled: Boolean(id),
    queryFn: async (): Promise<CampaignMember[]> => {
      const { data: members, error } = await supabase
        .from('campaign_members')
        .select('user_id, role, status, joined_on')
        .eq('campaign_id', id);
      if (error) throw error;
      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in(
          'id',
          members.map((m) => m.user_id),
        );
      if (pErr) throw pErr;
      const byId = new Map(profiles.map((p) => [p.id, p]));
      return members
        .map((m) => ({
          userId: m.user_id,
          role: m.role,
          status: m.status,
          joinedOn: m.joined_on,
          displayName: byId.get(m.user_id)?.display_name ?? 'Player',
          avatarUrl: byId.get(m.user_id)?.avatar_url ?? null,
        }))
        .sort(
          (a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || a.displayName.localeCompare(b.displayName),
        );
    },
  });
}

export function useCampaignInvites(id: string, enabled: boolean) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: campaignKeys.invites(id),
    enabled: enabled && Boolean(id),
    queryFn: async (): Promise<GroupInvite[]> => {
      const { data, error } = await supabase
        .from('group_invites')
        .select('*')
        .eq('campaign_id', id)
        .order('created_on', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

/** A mutation that refreshes every campaign query when it succeeds. */
function useCampaignMutation<TInput, TResult>(run: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
  });
}

export function useCreateCampaign() {
  const supabase = useSupabase();
  return useCampaignMutation(
    async (input: {
      groupId: string;
      name: string;
      description?: string;
      settings?: Partial<CampaignSettings>;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_campaign', {
        p_group_id: input.groupId,
        p_name: input.name,
        p_description: input.description ?? '',
        p_settings: input.settings ? { ...input.settings } : undefined,
      });
      if (error) throw error;
      return data;
    },
  );
}

export function useJoinCampaign() {
  const supabase = useSupabase();
  return useCampaignMutation(async (campaignId: string): Promise<MembershipStatus> => {
    const { data, error } = await supabase.rpc('join_campaign', { p_campaign_id: campaignId });
    if (error) throw error;
    return data;
  });
}

/** Edit the campaign. Unarchiving must be its own update: an archived campaign accepts no other change. */
export function useUpdateCampaign(id: string) {
  const supabase = useSupabase();
  return useCampaignMutation(
    async (
      patch: Partial<Pick<Campaign, 'name' | 'description' | 'labels' | 'archived_on'>> & {
        settings?: CampaignSettings;
      },
    ): Promise<void> => {
      const { settings, ...rest } = patch;
      const { error } = await supabase
        .from('campaigns')
        .update(settings ? { ...rest, settings: { ...settings } } : rest)
        .eq('id', id);
      if (error) throw error;
    },
  );
}

export function useDeleteCampaign() {
  const supabase = useSupabase();
  return useCampaignMutation(async (id: string): Promise<void> => {
    const { error, count } = await supabase.from('campaigns').delete({ count: 'exact' }).eq('id', id);
    if (error) throw error;
    if (count === 0) throw new Error("You don't have permission to delete this campaign.");
  });
}

export function useSetCampaignMemberRole(id: string) {
  const supabase = useSupabase();
  return useCampaignMutation(async (input: { userId: string; role: CampaignRole }): Promise<void> => {
    const { error } = await supabase.rpc('set_campaign_member_role', {
      p_campaign_id: id,
      p_user_id: input.userId,
      p_role: input.role,
    });
    if (error) throw error;
  });
}

export function useSetCampaignMemberStatus(id: string) {
  const supabase = useSupabase();
  return useCampaignMutation(async (input: { userId: string; status: MembershipStatus }): Promise<void> => {
    const { error } = await supabase.rpc('set_campaign_member_status', {
      p_campaign_id: id,
      p_user_id: input.userId,
      p_status: input.status,
    });
    if (error) throw error;
  });
}

export function useRemoveCampaignMember(id: string) {
  const supabase = useSupabase();
  return useCampaignMutation(async (userId: string): Promise<void> => {
    const { error } = await supabase.rpc('remove_campaign_member', { p_campaign_id: id, p_user_id: userId });
    if (error) throw error;
  });
}

export function useTransferCampaignOwnership(id: string) {
  const supabase = useSupabase();
  return useCampaignMutation(async (newOwnerId: string): Promise<void> => {
    const { error } = await supabase.rpc('transfer_campaign_ownership', {
      p_campaign_id: id,
      p_new_owner: newOwnerId,
    });
    if (error) throw error;
  });
}

export function useLeaveCampaign() {
  const supabase = useSupabase();
  return useCampaignMutation(async (campaignId: string): Promise<void> => {
    const { error } = await supabase.rpc('leave_campaign', { p_campaign_id: campaignId });
    if (error) throw error;
  });
}

export function useCreateCampaignInvite(id: string, groupId: string) {
  const supabase = useSupabase();
  return useCampaignMutation(
    async (input: {
      createdBy: string;
      message?: string;
      maxUses?: number | null;
      expiresOn?: string | null;
    }): Promise<GroupInvite> => {
      const { data, error } = await supabase
        .from('group_invites')
        .insert({
          group_id: groupId,
          campaign_id: id,
          created_by: input.createdBy,
          message: input.message || null,
          max_uses: input.maxUses ?? null,
          expires_on: input.expiresOn ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  );
}

export function useRevokeCampaignInvite(id: string) {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string): Promise<void> => {
      const { error } = await supabase
        .from('group_invites')
        .update({ revoked_on: new Date().toISOString() })
        .eq('id', inviteId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.invites(id) }),
  });
}
